import React, { useState, useRef, useEffect } from "react"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { FiSend, FiX, FiUser, FiTrash2 } from "react-icons/fi"
import { RiRobot2Line, RiSparklingLine } from "react-icons/ri"
import { GoogleGenerativeAI } from "@google/generative-ai"

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY)

function stripHtml(html) {
  if (!html) return ""
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
}

function buildSystemInstruction({ courseName, lectureTitle, lectureDescription }) {
  const descSnippet = lectureDescription
    ? `\nLecture overview: ${stripHtml(lectureDescription).slice(0, 600)}`
    : ""
  return `You are the KodeVedic AI Doubt Assistant — a focused tutor helping a student understand their current lecture.

Course: "${courseName || "this course"}"
Current Lecture: "${lectureTitle || "this lecture"}"${descSnippet}

Your role:
- Answer questions specifically about this lecture and related concepts
- Explain with working code examples when relevant (use markdown code blocks with language tags)
- Be concise but thorough — students want quick clarity, not essays
- If asked something off-topic, gently steer back to the lecture
- Be warm and encouraging — confusion is a normal part of learning`
}

const mdComponents = {
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-yellow-50 underline underline-offset-2 hover:opacity-80">
      {children}
    </a>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code className="rounded bg-richblack-800 px-1.5 py-0.5 font-mono text-xs text-caribbeangreen-300">{children}</code>
    ) : (
      <pre className="my-2 overflow-x-auto rounded-lg bg-richblack-900 p-3 font-mono text-xs leading-relaxed text-richblack-100">
        <code>{children}</code>
      </pre>
    ),
  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-richblack-5">{children}</strong>,
  h1: ({ children }) => <h1 className="mb-2 text-sm font-bold text-richblack-5">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-1.5 text-xs font-bold text-richblack-5">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-1 text-xs font-semibold text-richblack-100">{children}</h3>,
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-0.5">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="h-2 w-2 rounded-full bg-richblack-400 animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </div>
  )
}

const makeGreeting = (lectureTitle) => ({
  role: "assistant",
  content: `Hi! 👋 I'm your KodeVedicAI doubt assistant for **"${lectureTitle || "this lecture"}"**.\n\nAsk me anything about this topic — concepts, code, examples, or anything that's confusing you.`,
})

const toGeminiHistory = (msgs) => {
  const history = msgs.slice(1).map((m) => ({
    role: m.role === "user" ? "user" : "model",
    parts: [{ text: m.content }],
  }))
  while (history.length && history[history.length - 1].role === "user") history.pop()
  return history
}

export default function CourseAskPopup({ courseName, lectureTitle, lectureDescription }) {
  const { courseId, subSectionId } = useParams()
  const { user } = useSelector((state) => state.profile)

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([makeGreeting(lectureTitle)])
  const [prompt, setPrompt] = useState("")
  const [loading, setLoading] = useState(false)

  const chatSessionRef = useRef(null)
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)

  const storageKey = `kodevedic_course_ask_${courseId || "x"}_${subSectionId || "x"}`

  // Load saved messages and re-init Gemini whenever the lecture changes (and on mount)
  useEffect(() => {
    if (!subSectionId) return

    const key = `kodevedic_course_ask_${courseId}_${subSectionId}`
    let savedMsgs = [makeGreeting(lectureTitle)]
    try {
      const raw = localStorage.getItem(key)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed) && parsed.length > 0) savedMsgs = parsed
      }
    } catch { /* corrupted — fall through */ }

    setMessages(savedMsgs)
    setPrompt("")

    const sysInstr = buildSystemInstruction({ courseName, lectureTitle, lectureDescription })
    const model = genAI.getGenerativeModel({
      model: "gemini-2.5-flash-lite",
      systemInstruction: sysInstr,
    })
    chatSessionRef.current = model.startChat({
      history: toGeminiHistory(savedMsgs),
      generationConfig: { maxOutputTokens: 1024, temperature: 0.7 },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subSectionId])

  // Persist messages to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages.slice(-100)))
    } catch { /* storage full — ignore */ }
  }, [messages, storageKey])

  // Auto-scroll to latest message
  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, loading, open])

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 100) + "px"
  }, [prompt])

  const sendMessage = async (text) => {
    const trimmed = (text ?? prompt).trim()
    if (!trimmed || loading || !chatSessionRef.current) return

    setMessages((prev) => [...prev, { role: "user", content: trimmed }])
    setPrompt("")
    setLoading(true)

    try {
      const result = await chatSessionRef.current.sendMessage(trimmed)
      const reply = result.response.text()
      setMessages((prev) => [...prev, { role: "assistant", content: reply }])
    } catch (err) {
      console.error("Gemini error:", err)
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "I'm having trouble connecting right now. Please try again." },
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const clearChat = () => {
    const fresh = makeGreeting(lectureTitle)
    setMessages([fresh])
    localStorage.removeItem(storageKey)
    const sysInstr = buildSystemInstruction({ courseName, lectureTitle, lectureDescription })
    const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash-lite", systemInstruction: sysInstr })
    chatSessionRef.current = model.startChat({
      history: [],
      generationConfig: { maxOutputTokens: 1024, temperature: 0.7 },
    })
  }

  return (
    <>
      {/* ── Floating "Ask AI" button ── */}
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-yellow-50 px-4 py-2.5 text-sm font-semibold text-richblack-900 shadow-lg transition-all duration-200 hover:bg-yellow-100 active:scale-95 ${
          open ? "pointer-events-none opacity-0 scale-90" : "opacity-100 scale-100"
        }`}
        aria-label="Ask AI about this lecture"
      >
        <RiSparklingLine size={16} />
        Ask KodeVedic AI
      </button>

      {/* ── Chat popup ── */}
      <div
        className={`fixed z-50 flex flex-col overflow-hidden rounded-t-2xl border border-richblack-700 bg-richblack-900 shadow-2xl transition-all duration-300 ease-out
          bottom-0 right-0 h-[520px] w-full
          sm:bottom-6 sm:right-6 sm:h-[500px] sm:w-[380px] sm:rounded-2xl
          ${open ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0 pointer-events-none"}`}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center gap-2.5 border-b border-richblack-700 bg-richblack-800 px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-50 text-richblack-900">
            <RiRobot2Line size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold leading-tight text-richblack-5">KodeVedic AI Assistant</p>
            <p className="truncate text-[10px] text-white/70">
              {lectureTitle || "Current Lecture"}
            </p>
          </div>
          <button
            onClick={clearChat}
            title="Clear chat"
            className="rounded-lg p-1.5 text-richblack-400 transition-colors hover:bg-richblack-700 hover:text-richblack-100"
          >
            <FiTrash2 size={13} />
          </button>
          <button
            onClick={() => setOpen(false)}
            className="rounded-lg p-1.5 text-richblack-400 transition-colors hover:bg-richblack-700 hover:text-richblack-100"
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 space-y-4 overflow-y-auto px-3 py-3">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
            >
              <div
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs ${
                  msg.role === "user"
                    ? "bg-yellow-50 text-richblack-900"
                    : "bg-richblack-600 text-richblack-5"
                }`}
              >
                {msg.role === "user" ? (
                  user?.image ? (
                    <img src={user.image} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <FiUser size={12} />
                  )
                ) : (
                  <RiRobot2Line size={14} />
                )}
              </div>
              <div
                className={`max-w-[82%] rounded-2xl px-3 py-2 text-xs ${
                  msg.role === "user"
                    ? "rounded-tr-sm bg-yellow-50 text-richblack-900"
                    : "rounded-tl-sm bg-richblack-700 text-richblack-100"
                }`}
              >
                {msg.role === "assistant" ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {msg.content}
                  </ReactMarkdown>
                ) : (
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-richblack-600 text-richblack-5">
                <RiRobot2Line size={14} />
              </div>
              <div className="rounded-2xl rounded-tl-sm bg-richblack-700 px-3 py-2">
                <TypingDots />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="flex-shrink-0 border-t border-richblack-700 px-3 py-2.5">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              rows={1}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this lecture…"
              className="flex-1 resize-none overflow-hidden rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-xs text-richblack-5 placeholder:text-richblack-500 transition-colors focus:border-yellow-50/50 focus:outline-none"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!prompt.trim() || loading}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-yellow-50 text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FiSend size={14} />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
